import { gql } from '@urql/core';
import React, { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { AppStateUser } from '../../../client/providers/AppProvider';
import { useAuthenticatedQuery } from '../../../client/hooks';
import {
    ParentMyGamesDocument,
    ParentMyGamesQuery,
    ParentMyGamesQueryVariables,
} from '../../../generated/spin-graphql';
import { PictureWithFallback } from '../../sections/PictureWithFallback';
import { EditableGame } from './EditableGame';

type Props = {
    user: AppStateUser;
};
gql`
    query ParentMyGames($id: String!) {
        espin_user_by_pk(id: $id) {
            parent_profile {
                child_profiles(where: { user: { deleted_at: { _is_null: true } } }) {
                    spin_username
                    profile_image {
                        ...SpinContentImageDisplay
                    }
                    player_games(order_by: { primary: desc }) {
                        ...EditableGame
                    }
                }
            }
        }
    }
`;

export const ParentMyGames: React.FC<Props> = ({ user }) => {
    const { isReady, data } = useAuthenticatedQuery<ParentMyGamesQuery, ParentMyGamesQueryVariables>(
        {
            query: ParentMyGamesDocument,
            variables: useMemo(() => ({ id: user?.id }), [user]),
            pause: !!!user,
            requestPolicy: 'network-only', // always get freshest pls
        },
        ['espin_parent_profile', 'espin_player_profile', 'espin_player_game'],
    );

    if (!isReady) {
        return (
            <div className="my-games">
                <Skeleton height={228} borderRadius={15} />
            </div>
        );
    }

    const childProfiles = data.espin_user_by_pk.parent_profile?.child_profiles ?? [];

    return (
        <div className="my-players-games">
            {childProfiles.map((profile, index) => {
                const games = profile.player_games;
                const playerName = profile.spin_username;
                return (
                    <div key={`my-players-games-${index}`} className="my-player">
                        <div className="player-container">
                            <div className="image-wrapper">
                                <PictureWithFallback
                                    icon={profile.profile_image}
                                    width={40}
                                    height={40}
                                    alt={`${playerName}} profile picture`}
                                />
                            </div>
                            <h2 className="username">{playerName}</h2>
                        </div>
                        <div className="my-games">
                            {games && games.map((game, idx) => <EditableGame key={idx} game={game} />)}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
